import React, {useCallback, useEffect, useState} from "react";
import {Exist, Modal, Preloader} from "components";
import {useDispatch, useSelector} from "react-redux";
import styled from "styled-components";
import {ReactComponent as QatarLogo} from "assets/images/logos/qatarLogo.svg";
import {useTranslation} from "react-i18next";
import {LANGUAGE_PATH, trackAdobeAnalytics} from "modules";
import {
	checkIsFullPredictions,
	getFanSentimentsQuestionsWithAnswer,
	getSquads,
	showFanSentiments,
} from "apps/DRAW_PREDICTOR/modules/selectors";
import {handleModalState} from "apps/DRAW_PREDICTOR/modules/actions/modals";
import {
	fetchFanSentimentsJson,
	updateFanSentiment,
	saveFanSentiments,
} from "apps/DRAW_PREDICTOR/modules/actions";
import {filter, get, range, size} from "lodash";
import {FormControl, InputLabel, MenuItem, Select, SelectChangeEvent} from "@mui/material";
import {IFanDrawOption, IFanSentimentQuestions} from "apps/DRAW_PREDICTOR/modules/reducers";
import {media} from "assets/css/media";

const ModalInner = styled.div`
	text-align: center;
	padding: 20px;

	h2 {
		font-family: "Qatar2022";
		font-style: normal;
		font-weight: bold;
		font-size: 25px;
		line-height: 120%;
		margin: 30px auto 10px;
		max-width: 460px;
	}

	h3 {
		max-width: 500px;
		font-family: "Graphik";
		font-style: normal;
		font-weight: normal;
		font-size: 18px;
		line-height: 160%;
		margin: 0 auto 20px;
	}
`;

const Selects = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 20px;
	@media (max-width: ${media.mobile}) {
		flex-direction: column;
		margin-bottom: 40px;
	}
`;

const Question = styled.div``;
const IndexWrapper = styled.div`
	margin-top: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 14px;
`;
const QNumber = styled.div<{isActive: boolean}>`
	width: 24px;
	height: 24px;
	background: ${({isActive}) =>
		isActive ? "linear-gradient(227.93deg, #8A1538 0%, #8A1538 25%, #1C0111 100%)" : "#E5E5E5"};
	color: #fff;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;

	font-family: Roboto;
	font-style: normal;
	font-weight: normal;
	font-size: 12px;
	line-height: 166%;
	text-align: center;
	letter-spacing: 0.4px;
`;

interface IOptionsProps {
	question: IFanSentimentQuestions;
	answerIndex: number;
	options?: IFanDrawOption[];
}

const OptionsSelect: React.FC<IOptionsProps> = ({question, answerIndex, options}) => {
	const dispatch = useDispatch();
	const {t} = useTranslation(LANGUAGE_PATH);
	const placeholder = t("question.drop_down.team_placeholder");
	const [value, setValue] = useState("");

	const handleChange = (e: SelectChangeEvent) => {
		const {value, name} = e.target;
		const data = name.split("-");
		const questionId = parseInt(data[1], 10);
		const answerId = parseInt(data[2], 10);
		setValue(value);
		dispatch(
			updateFanSentiment({
				questionId,
				answerIndex: answerId,
				optionId: parseInt(value, 10),
			})
		);
	};
	if (!options) {
		return <Preloader />;
	}

	return (
		<FormControl variant="standard" sx={{m: 1, minWidth: 160, width: "100%"}}>
			<InputLabel id={`fan-sentiment-label-${answerIndex}`}>{placeholder}</InputLabel>
			<Select
				labelId={`fan-sentiment-label-${answerIndex}`}
				id={`fan-sentiment-${answerIndex}`}
				name={`question-${question.id}-${answerIndex}`}
				onChange={handleChange}
				value={value}
				label={placeholder}>
				<MenuItem value="">
					<em>{placeholder}</em>
				</MenuItem>
				{options.map((option) => (
					<MenuItem key={option.id} value={option.id}>
						<em>{option.text}</em>
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
};

const Questions = () => {
	const questions = useSelector(getFanSentimentsQuestionsWithAnswer);
	const [current, setCurrent] = useState(0);
	const {t} = useTranslation(LANGUAGE_PATH);
	const dispatch = useDispatch();
	const totalSize = size(questions);
	const isLast = totalSize === current - 1;
	const currentQuestion = get(questions, `[${current}]`, null) as IFanSentimentQuestions | null;
	const currentAnswers = get(currentQuestion, `answers`, []) as number[];
	const section = currentQuestion?.section;

	useEffect(() => {
		const withoutAnswer = filter(currentAnswers, (answer) => answer === 0);

		if (!size(withoutAnswer) && !isLast) {
			const newCurrent = current + 1;
			setCurrent(newCurrent);
		}
	}, [currentAnswers, current, isLast]);

	useEffect(() => {
		if (isLast) {
			dispatch(saveFanSentiments());
		}
	}, [dispatch, isLast]);

	useEffect(() => {
		if (section) {
			trackAdobeAnalytics({
				section: `:${section}`,
			});
		}
	}, [section]);

	if (!questions) {
		return <Preloader />;
	}
	return (
		<div>
			{questions.map((question, index) => (
				<Exist when={current === index} key={question.id}>
					<Question>
						<h3>{t(question.question)}</h3>
						<Selects>
							{range(question.answerNumber).map((answerIndex) => (
								<OptionsSelect
									key={answerIndex}
									question={question}
									answerIndex={answerIndex}
									options={
										question.options && question.options.length
											? question.options
											: question.playerOptionsArray
									}
								/>
							))}
						</Selects>
					</Question>
				</Exist>
			))}
			<IndexWrapper>
				{questions.map((q, index) => (
					<QNumber key={q.id} isActive={index <= current}>
						{index + 1}
					</QNumber>
				))}
			</IndexWrapper>
		</div>
	);
};

export const FanSentimentsModal: React.FC = () => {
	const show = useSelector(showFanSentiments);
	const squads = useSelector(getSquads);
	const {t} = useTranslation(LANGUAGE_PATH);
	const dispatch = useDispatch();
	const isFullPredictions = useSelector(checkIsFullPredictions);
	const [showed, setShowed] = useState(false);

	useEffect(() => {
		dispatch(fetchFanSentimentsJson());
	}, [dispatch]);

	useEffect(() => {
		if (isFullPredictions && !showed) {
			dispatch(handleModalState({fanSentiments: true}));
			setShowed(true);
		}
	}, [dispatch, isFullPredictions, showed]);

	const handleModalClose = useCallback(() => {
		dispatch(handleModalState({fanSentiments: false}));
		dispatch(handleModalState({drawConfirmation: true}));
	}, [dispatch]);

	if (!show || !squads.length) {
		return null;
	}

	return (
		<Modal onClose={handleModalClose}>
			<ModalInner>
				<QatarLogo />
				<h2>{t("question.modal.header")}</h2>
				<Questions />
			</ModalInner>
		</Modal>
	);
};
